import { useTranslation } from "react-i18next";
import { isWebsiteBuilderStretchElementData, TWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { isWebsiteBuilderElementFreeformParentNode, TWebsiteBuilderElementParentNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import { TWebsiteBuilderSection } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { useAppSelector } from "hooks/redux";
import { TWebsiteBuilderContextMenuSection, TWebsiteBuilderContextMenuSections } from "../../helpers/WebsiteBuilderContextMenu";
import { websiteBuilderCanBringToBackInFreeformParent, websiteBuilderCanBringToFrontInFreeformParent } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderFreeformParentNode";

export interface IUseElementViewContextMenuContentArgs {
  node: TWebsiteBuilderElement;
  parentNode: TWebsiteBuilderElementParentNode;
  sectionNode: TWebsiteBuilderSection;
  menuActive: boolean;
  openStretchSettings: () => void;
}

interface IUseElementViewContextMenuContent {
  sections: TWebsiteBuilderContextMenuSections;
}

export function useElementViewContextMenuContent(args: IUseElementViewContextMenuContentArgs): IUseElementViewContextMenuContent {
  const { t } = useTranslation();

  const { elementOperations, clipboard } = useWebsiteBuilderEditorContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  
  const modifierKey = window.navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";

  function getSections(): TWebsiteBuilderContextMenuSections {
    const sections: TWebsiteBuilderContextMenuSections = [];

    if (!args.menuActive) {
      return sections;
    }

    if (isWebsiteBuilderStretchElementData(args.node.element)) {
      sections.push([
        {
          label: t("Stretch"),
          shortcut: "",
          onClick: args.openStretchSettings,
          skipCloseAfterClick: true,
          disabled: args.parentNode.type !== "section",
        },
      ]);
    }

    const creationMenuSection: TWebsiteBuilderContextMenuSection = [
      {
        label: t("Copy"),
        shortcut: `${modifierKey} + C`,
        onClick: () => elementOperations.copyElement(args.node),
      },
      {
        label: t("Paste"),
        shortcut: `${modifierKey} + V`,
        onClick: () => elementOperations.pasteAtElement(args.node, args.parentNode, args.sectionNode, activePageId, deviceView),
        disabled: clipboard.copyType !== "element" && clipboard.copyType !== "container",
      },
      {
        label: t("Duplicate"),
        shortcut: `${modifierKey} + D`,
        onClick: () => elementOperations.duplicateElement(args.node, args.parentNode, args.sectionNode, activePageId, deviceView),
      },
    ];
  
    const zLayerMenuSection: TWebsiteBuilderContextMenuSection = [
      {
        label: t("Bring to front"),
        shortcut: (
          <>
            {modifierKey} + <FontAwesomeIcon icon={faArrowUp} />
          </>
        ),
        onClick: () => elementOperations.bringToFrontElement(args.node, args.parentNode, activePageId, deviceView),
        disabled:
          !isWebsiteBuilderElementFreeformParentNode(args.parentNode) ||
          !websiteBuilderCanBringToFrontInFreeformParent(args.node, args.parentNode),
      },
      {
        label: t("Bring to back"),
        shortcut: (
          <>
            {modifierKey} + <FontAwesomeIcon icon={faArrowDown} />
          </>
        ),
        onClick: () => elementOperations.bringToBackElement(args.node, args.parentNode, activePageId, deviceView),
        disabled:
          !isWebsiteBuilderElementFreeformParentNode(args.parentNode) ||
          !websiteBuilderCanBringToBackInFreeformParent(args.node, args.parentNode),
      },
    ];
  
    const deletionMenuSection: TWebsiteBuilderContextMenuSection = [
      {
        label: t("Delete"),
        shortcut: "Del",
        onClick: () => elementOperations.deleteElement(args.node, args.parentNode, activePageId, deviceView),
      },
      {
        label: t("Cut"),
        shortcut: `${modifierKey} + X`,
        onClick: () => elementOperations.cutElement(args.node, args.parentNode, activePageId, deviceView),
      },
    ];

    sections.push(creationMenuSection, zLayerMenuSection, deletionMenuSection);

    return sections;
  }

  const sections = getSections();

  return {
    sections,
  };
}